import { useState } from 'react';
import type { LeadPayload, LeadStatus } from '../../services/leads';
import { updateLeadStatus } from '../../services/leads';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

type LeadWithMeta = LeadPayload & { id: string; createdAt?: Date };

type LeadRowProps = {
  lead: LeadWithMeta;
  onUpdate: () => void;
};

const formatTimestamp = (date?: Date) => {
  if (!date) return '—';
  try {
    return new Intl.DateTimeFormat('it-IT', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toISOString();
  }
};

const statusLabels: Record<LeadStatus, string> = {
  new: 'Nuovo',
  in_progress: 'In lavorazione',
  done: 'Completato',
};

const statusColors: Record<LeadStatus, string> = {
  new: 'bg-blue-900/50 text-blue-200',
  in_progress: 'bg-yellow-900/50 text-yellow-200',
  done: 'bg-green-900/50 text-green-200',
};

export function LeadRow({ lead, onUpdate }: LeadRowProps) {
  const { user } = useAdminAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [status, setStatus] = useState<LeadStatus>(lead.status ?? 'new');
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!user?.email) return;
    try {
      setSaving(true);
      await updateLeadStatus(lead.id, status, user.email, notes);
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to update lead', error);
      alert('Errore durante il salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  const handleEmailClick = () => {
    const subject = encodeURIComponent(`Re: Richiesta informazioni RegAI - ${lead.name}`);
    const body = encodeURIComponent(
      `Gentile ${lead.name},\n\nGrazie per averci contattato.\n\n` +
        `Dettagli della tua richiesta:\n` +
        `- Ruolo: ${lead.role}\n` +
        `- Scuola: ${lead.school}\n` +
        `- Interessi: ${lead.interestLabels?.join(', ') ?? '—'}\n\n` +
        `Cordiali saluti,\nTeam RegAI`
    );
    window.location.href = `mailto:${lead.email}?subject=${subject}&body=${body}`;
  };

  return (
    <tr className="hover:bg-slate-800/60 transition">
      <td className="px-4 py-3 align-top text-sm text-slate-300">
        {formatTimestamp(lead.createdAt)}
      </td>
      <td className="px-4 py-3 align-top text-sm">
        <div className="font-semibold text-slate-100">{lead.name}</div>
        <div className="text-slate-400">{lead.email}</div>
        {lead.phone && <div className="text-slate-400">{lead.phone}</div>}
      </td>
      <td className="px-4 py-3 align-top text-sm text-slate-300">{lead.role}</td>
      <td className="px-4 py-3 align-top text-sm text-slate-300">
        <div>{lead.school}</div>
        {lead.ordine && <div className="text-slate-400">{lead.ordine}</div>}
        {lead.location && <div className="text-slate-400">{lead.location}</div>}
        {lead.size && <div className="text-slate-400">dimensione: {lead.size}</div>}
      </td>
      <td className="px-4 py-3 align-top text-sm text-slate-300">
        <ul className="space-y-1">
          {lead.interestLabels?.map((interest) => (
            <li key={interest} className="bg-slate-800/70 text-slate-200 px-2 py-1 rounded text-xs">
              {interest}
            </li>
          ))}
        </ul>
      </td>
      <td className="px-4 py-3 align-top text-sm text-slate-300 whitespace-pre-wrap max-w-xs">
        {lead.message || '—'}
      </td>
      <td className="px-4 py-3 align-top text-sm">
        {isEditing ? (
          <div className="space-y-2 min-w-[200px]">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as LeadStatus)}
              className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-200 text-xs"
            >
              <option value="new">Nuovo</option>
              <option value="in_progress">In lavorazione</option>
              <option value="done">Completato</option>
            </select>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Note interne..."
              rows={3}
              className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-200 text-xs"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded text-xs text-white"
              >
                {saving ? 'Salvataggio…' : 'Salva'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setStatus(lead.status ?? 'new');
                  setNotes(lead.notes ?? '');
                }}
                className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded text-xs text-slate-200"
              >
                Annulla
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className={`inline-block px-2 py-1 rounded text-xs ${statusColors[lead.status ?? 'new']}`}>
              {statusLabels[lead.status ?? 'new']}
            </div>
            {lead.notes && <div className="text-xs text-slate-400 italic">{lead.notes}</div>}
            {lead.processedBy && (
              <div className="text-xs text-slate-500">
                Gestito da {lead.processedBy} il {formatTimestamp(lead.processedAt)}
              </div>
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-3 align-top text-sm">
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleEmailClick}
            className="px-3 py-1 bg-blue-700 hover:bg-blue-600 rounded text-xs text-white whitespace-nowrap"
          >
            📧 Rispondi
          </button>
          <button
            type="button"
            onClick={() => setIsEditing(!isEditing)}
            className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded text-xs text-slate-200 whitespace-nowrap"
          >
            {isEditing ? 'Chiudi' : '✏️ Gestisci'}
          </button>
        </div>
      </td>
    </tr>
  );
}
